"use client"
import React, { useMemo } from "react";
import { EditOutlined, FileDoneOutlined, HistoryOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Input, TableColumnsType, Tooltip, Select } from "antd";
import dayjs from "dayjs";
import DateEditor from "../../components/common/dateEditor";
import { fmtMoney } from "../../helpers/commonHelpers";
import type { CobPayerListItem } from "./CobPayers/cobPayers";
import type { CobPayerAuditHistory } from "./CobPayers/createCobPayer";
import type { SorterResult } from "antd/es/table/interface";

const { Option } = Select;

export const renderCobPayerChangeRequestStatus = (value?: string) => {
    const normalizedStatus = value?.trim().toLowerCase();
    if (!normalizedStatus) return null;

    const dotColor = normalizedStatus === "approved"
        ? "#22c55e"
        : normalizedStatus === "rejected"
            ? "#ef4444"
            : "#f59e0b";

    return (
        <span style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            borderRadius: "999px",
            border: "1px solid #e5e7eb",
            padding: "3px 8px",
            fontSize: "12px",
            fontWeight: 600
        }}>
            <span style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                display: "inline-block",
                backgroundColor: dotColor
            }} />
            {value}
        </span>
    );
};

export const CobPayerAuditHistoryColumns: TableColumnsType<CobPayerAuditHistory> = [
    {
        title: "Changed Date",
        dataIndex: "changedDate",
        width: 170,
        render: (value?: string) => value ? dayjs(value).format("MM/DD/YYYY hh:mm A") : ""
    },
    { title: "Changed By", dataIndex: "changedBy", width: 140, ellipsis: true },
    {
        title: "Approved Date",
        dataIndex: "approvalDate",
        width: 170,
        render: (value?: string) => value ? dayjs(value).format("MM/DD/YYYY hh:mm A") : ""
    },
    { title: "Approved By", dataIndex: "approvedBy", width: 140, ellipsis: true },
    { title: "Action", dataIndex: "action", width: 90 },
    { title: "Field Name", dataIndex: "fieldName", width: 160, ellipsis: true },
    {
        title: "Old Value",
        dataIndex: "oldValue",
        width: 180,
        render: (value?: string) => value && value.length > 20 ? (
            <Tooltip title={value} placement="topLeft">
                <span className="billing-table-cell-ellipsis">{value}</span>
            </Tooltip>
        ) : value
    },
    {
        title: "New Value",
        dataIndex: "newValue",
        width: 180,
        render: (value?: string) => value && value.length > 20 ? (
            <Tooltip title={value} placement="topLeft">
                <span className="billing-table-cell-ellipsis">{value}</span>
            </Tooltip>
        ) : value
    }
];

export const createCobPayerListColumns = (
    onEdit: (record: CobPayerListItem) => void,
    sortedInfo: SorterResult<CobPayerListItem>
): TableColumnsType<CobPayerListItem> => [
    {
        title: "Payer Code",
        dataIndex: "code",
        sorter: true,
        sortOrder: sortedInfo.field === "code" ? sortedInfo.order : null,
        width: 130,
        ellipsis: true
    },
    {
        title: "Payer Name",
        dataIndex: "name",
        sorter: true,
        sortOrder: sortedInfo.field === "name" ? sortedInfo.order : null,
        width: 220,
        ellipsis: true
    },
    {
        title: "Invoice Submission Type",
        dataIndex: "invoiceType",
        sorter: true,
        sortOrder: sortedInfo.field === "invoiceType" ? sortedInfo.order : null,
        width: 170,
        ellipsis: true
    },
    {
        title: "Is Active",
        dataIndex: "isActive",
        sorter: true,
        sortOrder: sortedInfo.field === "isActive" ? sortedInfo.order : null,
        width: 80,
        render: (value: boolean) => value ? "Yes" : "No"
    },
    {
        title: "Change Request Status",
        dataIndex: "changeRequestStatus",
        sorter: true,
        sortOrder: sortedInfo.field === "changeRequestStatus" ? sortedInfo.order : null,
        width: 140,
        ellipsis: true,
        render: renderCobPayerChangeRequestStatus
    },
    {
        title: "Change Request Date",
        dataIndex: "changeRequestDate",
        sorter: true,
        sortOrder: sortedInfo.field === "changeRequestDate" ? sortedInfo.order : null,
        width: 210,
        render: (value?: string) => value ? dayjs(value).format("MM/DD/YYYY hh:mm A") : ""
    },
    {
        title: "Modified Date",
        dataIndex: "modifiedDate",
        sorter: true,
        sortOrder: sortedInfo.field === "modifiedDate" ? sortedInfo.order : null,
        width: 170,
        render: (value?: string) => value ? dayjs(value).format("MM/DD/YYYY hh:mm A") : ""
    },
    {
        title: "Created Date",
        dataIndex: "createdDate",
        sorter: true,
        sortOrder: sortedInfo.field === "createdDate" ? sortedInfo.order : null,
        width: 170,
        render: (value?: string) => value ? dayjs(value).format("MM/DD/YYYY hh:mm A") : ""
    },
    {
        title: "Edit",
        dataIndex: "id",
        key: "edit",
        width: 80,
        fixed: "right" as const,
        align: "center" as const,
        render: (_: number, record: CobPayerListItem) => (
            <Button
                aria-label={`Edit ${record.name}`}
                onClick={() => onEdit(record)}
                style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9", color: "rgba(0, 0, 0, 0.88)" }}
                type="primary"
            >
                <EditOutlined />
            </Button>
        )
    }
];

export const AuditTableColumns: TableColumnsType<any> = [
    {
        title: 'Date/Time',
        dataIndex: 'date',
        render: (text) => dayjs(text).format('MM/DD/YYYY hh:mm A'),
        sorter: true
    },
    {
        title: 'User Id',
        dataIndex: 'userName',
        sorter: true
    },
    {
        title: 'Field',
        dataIndex: 'fieldName',
        sorter: true
    },
    {
        title: 'Original Value',
        dataIndex: 'oldValue',
        render: (text, record) => {
            return (
                text && text.length > 20 ?
                    <Tooltip title={text} placement="topLeft">
                        <span className="billing-table-cell-ellipsis">
                            {text}
                        </span>
                    </Tooltip>
                    : text
            );
        }
    },
    {
        title: 'New value',
        dataIndex: 'newValue',
         render: (text, record) => {
            return (
                text && text.length > 20 ?
                    <Tooltip title={text} placement="topLeft">
                        <span className="billing-table-cell-ellipsis">
                            {text}
                        </span>
                    </Tooltip>
                    : text
            );
        }
    },
    {
        title: 'Note Text',
        dataIndex: 'note',
        render: (text, record) => {
            return (
                text && text.length > 20 ?
                    <Tooltip title={text} placement="topLeft">
                        <span className="billing-table-cell-ellipsis">
                            {text}
                        </span>
                    </Tooltip>
                    : text
            );

        }
    }
];

export const DownloadFileColumns: TableColumnsType<any> = [
    {
        title: 'Date/Time',
        dataIndex: 'date',
        render: (text) => dayjs(text).format('MM/DD/YYYY hh:mm A'),
        sorter: true
    },
    {
        title: 'System User',
        dataIndex: 'userName',
        sorter: true
    },
    {
        title: 'File Name',
        dataIndex: 'fileName',
        sorter: true
    }
];

export const UploadFileColumns: TableColumnsType<any> = [
    {
        title: 'Date/Time',
        dataIndex: 'date',
        render: (text) => dayjs(text).format('MM/DD/YYYY hh:mm A'),
        sorter: true
    },
    {
        title: 'System User',
        dataIndex: 'userName',
        sorter: true
    },
    {
        title: 'File Name',
        dataIndex: 'fileName',
        sorter: true
    },
    {
        title: 'Note',
        dataIndex: 'note'
    }
];

export const DownloadDataColumns: TableColumnsType<any> = [
    {
        title: 'Date/Time',
        dataIndex: 'date',
        render: (text) => dayjs(text).format('MM/DD/YYYY hh:mm A'),
        sorter: true
    },
    {
        title: 'System User',
        dataIndex: 'userName',
        sorter: true
    },
    {
        title: 'File Name',
        dataIndex: 'fileName',
        sorter: true
    },
    {
        title: 'Status',
        dataIndex: 'status',
        sorter: true
    }
];

export const GenerateSubmissionFilesColumns: TableColumnsType<any> = [
    {
        title: 'Date/Time',
        dataIndex: 'date',
        render: (text) => dayjs(text).format('MM/DD/YYYY hh:mm A'),
        sorter: true
    },
    {
        title: 'System User',
        dataIndex: 'userName',
        onCell: (
            () => ({ style: { overflowWrap: 'anywhere' } })
        ),
        sorter: true
    },
    {
        title: 'File Name',
        dataIndex: 'fileName',
        onCell: (
            () => ({ style: { overflowWrap: 'anywhere' } })
        ),
        sorter: true
    },
    {
        title: 'File Type',
        dataIndex: 'fileType',
        sorter: true
    },
    {
        title: 'Status',
        dataIndex: 'status',
        sorter: true
    }
];

export const MakeCorrectionColumns: TableColumnsType<any> = [
    {
        title: 'Date Of Service',
        dataIndex: 'dateOfService',
        render: (text) => dayjs(text).format('MM/DD/YYYY'),
        sorter: true
    },
    {
        title: 'CTC Trip Id',
        dataIndex: 'ctcTripId',
        sorter: true
    },
    {
        title: 'Member Id',
        dataIndex: 'memberId',
        sorter: true
    },
    {
        title: 'Status',
        dataIndex: 'tripLegStatus',
        sorter: true
    },
    {
        title: 'Billed Amount',
        dataIndex: 'billedAmount',
        sorter: true
    },
    {
        title: 'Billable',
        dataIndex: 'billable',
        sorter: true
    },
    {
        title: 'Level Of Service',
        dataIndex: 'levelOfService',
        sorter: true
    }
];

export const ReimbursementColumns: TableColumnsType<any> = [
    {
        title: 'CTC Trip Id',
        dataIndex: 'ctcTripId',
        sorter: true
    },
    {
        title: 'Date Of Service',
        dataIndex: 'dateOfService',
        render: (text) => dayjs(text).format('MM/DD/YYYY'),
        sorter: true,
        defaultSortOrder: "descend"
    },
    {
        title: 'Member Id',
        dataIndex: 'memberId',
        sorter: true
    },
    {
        title: 'Requested Date',
        dataIndex: 'requestedDate',
        render: (text) => text? dayjs(text).format('MM/DD/YYYY'):'',
        sorter: true
    },
    {
        title: 'Check Date',
        dataIndex: 'checkDate',
        render: (text) => text ? dayjs(text).format('MM/DD/YYYY') : '',
        sorter: true
    },
    {
        title: 'Check Clear Date',
        dataIndex: 'checkClearDate',
        render: (text) => text ? dayjs(text).format('MM/DD/YYYY') : '',
        sorter: true
    },
    {
        title: 'Check Amount',
        dataIndex: 'checkAmount',
        sorter: true,
        align: "right", // Align numbers to the right
        render: (value) => 
            value ==null || isNaN(value)?'': new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
            }).format(value)
    },
    {
        title: 'Check Number',
        dataIndex: 'checkNumber',
        align: "right", // Align numbers to the right
        sorter: true
    },
    {
        title: 'Billed',
        dataIndex: 'billed',
        sorter: true
    }
];

export const Payment835AuditTableColumns: TableColumnsType<any> = [
    {
        title: 'Date/Time',
        dataIndex: 'date',
        render: (text) => dayjs(text).format('MM/DD/YYYY hh:mm A'),
        sorter: true
    },
    {
        title: 'User Id',
        dataIndex: 'userName',
        sorter: true
    },
    {
        title: 'Field',
        dataIndex: 'fieldName',
        sorter: true
    },
    {
        title: 'Action',
        dataIndex: 'action',
        sorter: true
    },
    {
        title: 'Original Value',
        dataIndex: 'oldValue'
    },
    {
        title: 'New Value',
        dataIndex: 'newValue'
    },
    {
        title: 'Note Text',
        dataIndex: 'note'
    }
];

export const DownloadPayment835FileColumns: TableColumnsType<any> = [
    {
        title: 'File Name',
        dataIndex: 'fileName',
        sorter: true
    },
    {
        title: 'File Type',
        dataIndex: 'fileType',
        sorter: true
    }
];

export const buildCcahBreakdownColumns = () => [
    { title: "No.", dataIndex: "seq", key: "seq", width: 90 },
    { title: "CCS/VIP", dataIndex: "category", key: "category" },
    {
        title: "Billed Amount",
        dataIndex: "finalBilledAmount",
        key: "finalBilledAmount",
        align: "right" as const,
        render: (v: number) => fmtMoney(v),
    },
];

export const buildCcahIncentiveColumns = (opts: {
    readOnly: boolean;
    onSLAChange: (value: number, recordKey: string) => void;}) => [
    { title: "Item", dataIndex: "item", key: "item" },
    {
        title: "SLA%",
        dataIndex: "sla",
        key: "sla",
        render: (sla: number, record: any) =>
            opts.readOnly ? (
                <span>{sla}%</span>
            ) : (
                <Select
                    value={sla}
                    onChange={(val) => opts.onSLAChange(val, record.key)}
                    style={{ width: 100 }}
                    size="small"
                >
                    {[98, 99, 100].map((v) => (
                        <Option key={v} value={v}>
                            {v}%
                        </Option>
                    ))}
                </Select>
            ),
    },
    { title: "Qualify for Incentive", dataIndex: "qualify", key: "qualify" },
    {
        title: "Billed Amount",
        dataIndex: "billedAmount",
        key: "billedAmount",
        align: "right" as const,
        render: (v: number) => fmtMoney(v),
    },
    {
        title: "Incentive %",
        dataIndex: "incentivePercent",
        key: "incentivePercent",
        align: "right" as const,
        render: (v: number) => `${(v * 100).toFixed(2)}%`,
    },
    {
        title: "Calculated Incentive",
        dataIndex: "calculatedIncentive",
        key: "calculatedIncentive",
        align: "right" as const,
        render: (v: number) => fmtMoney(v),
    },
    ];

export  const ImportBusPassBaseColumns = [
    { title: "Pass Number", dataIndex: "passNumber", sorter: true, filter: true, dataType: "string", width: 160 },
    { title: "Member ID", dataIndex: "memberId", sorter: true, filter: true, dataType: "string", width: 150 },
    { title: "Vendor", dataIndex: "vendor", sorter: true, filter: true, dataType: "string", width: 150 },
    { title: "Inventory Type", dataIndex: "inventoryType", sorter: true, filter: true, dataType: "string", width: 170 },
    { title: "Billing Type", dataIndex: "billingType", sorter: true, filter: true, dataType: "string", width: 150 },
    { title: "Quantity", dataIndex: "quantity", sorter: true, filter: true, format: "Number", width: 120 },
    { title: "Ticket Cost", dataIndex: "ticketCost", sorter: true, filter: true, format: "Currency", width: 140 },
    { title: "Admin (Qty)", dataIndex: "adminQty", sorter: true, filter: true, format: "Number", width: 130 },
    { title: "Total Charge", dataIndex: "totalCharge", sorter: true, filter: true, format: "Currency", width: 150 },
    { title: "Mailing Date", dataIndex: "mailingDate", sorter: true, filter: true, dataType: "date", format: "MM/DD/YYYY", width: 145 },
    { title: "First Trip Date", dataIndex: "firstTripDate", sorter: true, filter: true, dataType: "date", format: "MM/DD/YYYY", width: 145 },
    { title: "Billable", dataIndex: "billable", sorter: true, filter: true, dataType: "string", width: 120 },
    { title: "Non-billable Reason", dataIndex: "nonbillableDesc", sorter: true, filter: true, dataType: "string", width: 220 },
    { title: "Billing Note", dataIndex: "billingNote", sorter: true, filter: true, dataType: "string", width: 220 },
    { title: "Format Error", dataIndex: "formatErrorMessage", sorter: true, filter: true, dataType: "string", width: 300 }
];

export const VendorPaymentFileColumnsConfig = [
    { title: "File Name", dataIndex: "fileName", sorter: true, dataType: "string", wrapText: true, width: 260 },
    { title: "Status", dataIndex: "status", sorter: true, dataType: "string" },
    { title: "Imported Count", dataIndex: "recordCount", sorter: true, dataType: "number" },
    { title: "Processed Count", dataIndex: "processedRecordCount", sorter: true, dataType: "number"  },
    { title: "Imported Date", dataIndex: "createdOn", sorter: true, dataType: "date", format: "MM/DD/YYYY hh:mm A" },
    { title: "Imported By", dataIndex: "createdBy", sorter: true, dataType: "string" },
];

export const VendorPaymentReviewColumnsConfig = [
    { title: "Process Date", dataIndex: "processDate", sorter: true, filter: true, dataType: "date", format: "MM/DD/YYYY", width: 50, fixed: true },
    { title: "Vendor", dataIndex: "vendor", sorter: true, filter: true, dataType: "string", fixed: true, wrapText: true, width: 100 },
    { title: "Tax ID", dataIndex: "vendorTaxId", sorter: true, filter: true, dataType: "string", width: 50, fixed: true },
    { title: "Invoice Number", dataIndex: "invoiceNumber", sorter: true, filter: true, dataType: "string", fixed: true, width: 100, wrapText: true },
    { title: "Disbursement Reference", dataIndex: "disbursementReference", sorter: true, filter: true, dataType: "string", width: 100 },
    { title: "Check Date", dataIndex: "checkDate", sorter: true, filter: true, dataType: "date", format: "MM/DD/YYYY", width: 50 },
    { title: "Amount", dataIndex: "amount", sorter: true, filter: true, format: "Currency", width: 60 },
    { title: "EOP Number", dataIndex: "eopNumber", sorter: true, filter: true, dataType: "string", width: 120, wrapText: true},
    { title: "EOP Amount", dataIndex: "eopAmount", sorter: true, filter: true, format: "Currency", width: 80 },
    { title: "Mapping Status", dataIndex: "mappingStatus", sorter: true, filter: true, dataType: "string", width: 60 },
    { title: "Mapping Reason", dataIndex: "mappingReason", sorter: true, filter: true, dataType: "string", width: 80 },
    { title: "Payment Reference", dataIndex: "paymentReference", sorter: true, filter: true, dataType: "string", width: 120 },
    { title: "Payment Method", dataIndex: "paymentMethod", sorter: true, filter: true, dataType: "string", width: 100 }    
    
];

export const BusPassAssignmentColumnsConfig = [
    { title: "Member Name", dataIndex: "MbrName", dataType: "memberLink", filter: false, sorter: false, width: 180 },
    { title: "Member ID", dataIndex: "MbrID", dataType: "string", filter: false, sorter: false, width: 140 },
    { title: "Address", dataIndex: "FormattedAddress", dataType: "string", filter: false, sorter: false, width: 350 },
    { title: "Pass Format", dataIndex: "PassFormat", dataType: "string", filter: false, sorter: false, width: 150 },
    { title: "Pass Type", dataIndex: "PassType", dataType: "string", filter: false, sorter: false, width: 150 },
    { title: "Number of Days", dataIndex: "NumberOfDays", dataType: "string", filter: false, sorter: false, width: 160 },
    { title: "Escorts", dataIndex: "Escorts", dataType: "string", filter: false, sorter: false, width: 120 },
    { title: "Bus Pass Cost", dataIndex: "BusPassCost", dataType: "string", filter: false, sorter: false, width: 150 },
    { title: "First Date of Service", dataIndex: "FirstDateofService", dataType: "string", filter: false, sorter: false, width: 180 },
    { title: "Last Date of Service", dataIndex: "LastDateofService", dataType: "string", filter: false, sorter: false, width: 180 },
    { title: "Date Sent", dataIndex: "DateSent", dataType: "string", filter: false, sorter: false, width: 150 },
    { title: "Transit Agency", dataIndex: "TransitAgency", dataType: "string", filter: false, sorter: false, width: 180 },
    { title: "Bus Line Numbers", dataIndex: "BusLineNumbers", dataType: "string", filter: false, sorter: false, width: 180 },
    { title: "Vendor", dataIndex: "SubContractorName", dataType: "string", filter: false, sorter: false, width: 220 },
    { title: "Reservation ID", dataIndex: "ReservationID", dataType: "reservationLinks", filter: false, sorter: false, width: 220 },
    { title: "Trip Legs", dataIndex: "TripLegIDs", dataType: "string", filter: false, sorter: false, width: 180 },
    { title: "Preferred Pass Tag", dataIndex: "LatestTripTagNames", dataType: "string", filter: false, sorter: false, width: 200 },
    { title: "Reservation Note", dataIndex: "CCERRTSMbrNote", dataType: "string", filter: false, sorter: false, width: 220 },
    { title: "Client Name", dataIndex: "ClientName", dataType: "string", filter: false, sorter: false, width: 180 },
    { title: "Ticket Number", dataIndex: "TicketNumber", dataType: "string", filter: false, sorter: false, width: 160 },
    { title: "Returned", dataIndex: "Returned", dataType: "string", filter: false, sorter: false, width: 130 },
    { title: "Resent", dataIndex: "Resent", dataType: "string", filter: false, sorter: false, width: 120 },
    { title: "Billed Date", dataIndex: "BilledDate", dataType: "string", filter: false, sorter: false, width: 150 },
    { title: "Billed Amount", dataIndex: "BilledAmount", dataType: "string", filter: false, sorter: false, width: 150 },
    { title: "Paid Date", dataIndex: "PaidDate", dataType: "string", filter: false, sorter: false, width: 150 },
    { title: "Action", dataIndex: "BusPassActions", filter: false, dataType: "busPassActions", sorter: false, width: 110, fixed: "right" }
];

const getColumnSearchProps = (title, dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${title}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Button
          type="primary"
          onClick={() => confirm()}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Search
        </Button>
        <Button
          onClick={() => { setSelectedKeys([]); clearFilters(); confirm(); }}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) => {
        if (!record[dataIndex] && record[dataIndex] !== 0) return false;
        
        const searchValue = value.toString().toLowerCase().trim();
        const recordValue = record[dataIndex];
        
        // Convert record value to string for comparison
        const recordString = recordValue.toString().toLowerCase();
        
        // For numeric values, also try exact numeric comparison
        if (!isNaN(recordValue) && !isNaN(searchValue)) {
          const numericSearch = parseFloat(searchValue);
          const numericRecord = parseFloat(recordValue);
          
          // Exact match or string contains match
          return numericRecord === numericSearch || recordString.includes(searchValue);
        }
        
        // String comparison for non-numeric values
        return recordString.includes(searchValue);
      },
	});

const toSortableDate = (value: any): number => {
    if (value === null || value === undefined || value === '') return 0;
    const parsedDate = dayjs(value);
    return parsedDate.isValid() ? parsedDate.valueOf() : 0;
};

const toSortableNumber = (value: any): number => {
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : 0;
    }

    if (value === null || value === undefined || value === "") {
        return 0;
    }

    const normalized = String(value).replace(/[$,\s]/g, "");
    const parsedNumber = Number.parseFloat(normalized);
    return Number.isFinite(parsedNumber) ? parsedNumber : 0;
};

interface TableConfigOptions {
    onComponentAction?: (componentName: string, record: any, columnConfig?: any) => void;
}

export const createTableConfig = (config: any, handleTableChange?: (value: any, record?: any, fieldName?: string) => void, options?: TableConfigOptions) => {
    let columns: any = [];
    config.forEach((x) => {
        if (!x.hidden) {
            if (x.dataType?.toLowerCase() == 'dateEditor') {
                let colConfig = {
                    title: x.title,
                    dataIndex: x.dataIndex,
                    hidden: x.hidden ?? false,
                    sorter: x.sorter
                        ? (a, b) =>
                            x.dataType === 'string'
                                ? (a[x.dataIndex] || "").localeCompare(b[x.dataIndex] || "")
                                : (a[x.dataIndex] ?? 0) - (b[x.dataIndex] ?? 0)
                        : false,
                    width: x.width ?? 140,
                    fixed: x.fixed ?? false,
                    onCell: (record) => ({
                        style: { background: record.oldData && ((record.oldData[x.dataIndex] || record[x.dataIndex]) != '') && record.oldData[x.dataIndex] != record[x.dataIndex] ? "#66B966" : "" }
                    }),
                    render: (text, record) => (
                        <>
                            <DateEditor value={text} onChange={(value) => handleTableChange?.(value, record, x.dataIndex)} />
                        </>
                    )
                }
                if (x.filter) {
                    colConfig = { ...colConfig, ...getColumnSearchProps(x.title, x.dataIndex) }
                }
                columns.push(
                    colConfig
                );
            } else if (x.dataType?.toLowerCase() === 'buspassactions') {
                columns.push({
                    title: x.title,
                    dataIndex: x.dataIndex || x.title,
                    hidden: x.hidden ?? false,
                    sorter: false,
                    width: x.width ?? 110,
                    fixed: x.fixed ?? false,
                    align: "center",
                    render: (_text, record) => (
                        <span style={{ display: "inline-flex", gap: 4, alignItems: "center", justifyContent: "center" }}>
                            <Tooltip title="History">
                                <Button
                                    type="text"
                                    size="small"
                                    style={{ color: "#1677ff" }}
                                    aria-label="History"
                                    icon={<HistoryOutlined />}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        handleTableChange?.("BusPassHistory", record, "MbrID");
                                    }}
                                />
                            </Tooltip>
                            <Tooltip title="Bus Pass Process">
                                <Button
                                    type="text"
                                    size="small"
                                    style={{ color: "#1677ff" }}
                                    aria-label="Bus Pass Process"
                                    icon={<FileDoneOutlined />}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        handleTableChange?.("BusPassReportForm", record, "MbrID");
                                    }}
                                />
                            </Tooltip>
                        </span>
                    )
                });
            } else if (x.dataType?.toLowerCase() == 'modal') {
                columns.push({
                    title: x.title,
                    dataIndex: x.dataIndex || x.uniqueId || x.title,
                    hidden: x.hidden ?? false,
                    sorter: false,
                    width: x.width ?? 140,
                    fixed: x.fixed ?? false,
                    render: (_text, record) => (
                        <Button
                            type="link"
                            size="small"
                            onClick={() => handleTableChange?.(x.component, record, x.uniqueId)}
                        >
                            {x.label ?? "Process"}
                        </Button>
                    )
                });
            } else if (x.dataType?.toLowerCase() == 'navigation') {
                let colConfig = {
                    title: x.title,
                    dataIndex: x.dataIndex,
                    hidden: x.hidden ?? false,
                    sorter: x.sorter
                        ? (a, b) =>
                            x.dataType === 'string'
                                ? (a[x.dataIndex] || "").localeCompare(b[x.dataIndex] || "")
                                : (a[x.dataIndex] ?? 0) - (b[x.dataIndex] ?? 0)
                        : false,
                    width: x.width ?? 140,
                    fixed: x.fixed ?? false,
                    align: x.format == 'Number' ? 'right' : "left",
                    onCell: (record) => ({
                        style: { background: record.oldData && ((record.oldData[x.dataIndex] || record[x.dataIndex]) != '') && record.oldData[x.dataIndex] != record[x.dataIndex] ? "#66B966" : "" }
                    }),
                    render: (text, record) => (
                        <>
                            <a target="_blank" href={record[x.url]}>
                                {text}
                            </a>
                        </>
                    )
                }
                if (x.filter) {
                    colConfig = { ...colConfig, ...getColumnSearchProps(x.title, x.dataIndex) }
                }
                columns.push(
                    colConfig
                );
            } else if (x.dataType?.toLowerCase() === "reservationlinks") {
                columns.push({
                    title: x.title,
                    dataIndex: x.dataIndex,
                    hidden: x.hidden ?? false,
                    sorter: false,
                    width: x.width ?? 220,
                    fixed: x.fixed ?? false,
                    render: (text) => {
                        const baseUrl = window.localStorage.getItem("Referrer") ?? "";
                        const ids = String(text ?? "")
                            .split(/\s*,\s*/)
                            .map(x => x.trim())
                            .filter(Boolean);

                        if (!ids.length) {
                            return "";
                        }

                        return (
                            <span>
                                {ids.map((id, index) => (
                                    <React.Fragment key={`${id}-${index}`}>
                                        <a
                                            href={`${baseUrl}/Reservation/Reservation/Detail/${btoa(id.replace(/^CTC-/i, ""))}`}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {id}
                                        </a>
                                        {index < ids.length - 1 ? ", " : ""}
                                    </React.Fragment>
                                ))}
                            </span>
                        );
                    }
                });
            } else if (x.dataType?.toLowerCase() === "memberlink") {
                columns.push({
                    title: x.title,
                    dataIndex: x.dataIndex,
                    hidden: x.hidden ?? false,
                    sorter: false,
                    width: x.width ?? 180,
                    fixed: x.fixed ?? false,
                    render: (text, record) => {
                        const memberNavigationId = record?.MemberId ?? record?.MemberID ?? record?.memberId;

                        if (!memberNavigationId) {
                            return text;
                        }

                        const baseUrl = window.localStorage.getItem("Referrer") ?? "";

                        return (
                            <a
                                href={`${baseUrl}/Member/Member/Detail?prm=${btoa(String(memberNavigationId))}`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {text || record?.MbrID || memberNavigationId}
                            </a>
                        );
                    }
                });
            } else if (x.dataType?.toLowerCase() === 'modal') {
                const colConfig = {
                    title: x.title,
                    dataIndex: x.dataIndex,
                    hidden: x.hidden ?? false,
                    sorter: x.sorter ?? false,
                    width: x.width ?? 160,
                    fixed: x.fixed ?? false,
                    align: "left",
                    render: (_text, record) => {
                        const labelText = x.dataIndex && record?.[x.dataIndex]
                            ? record[x.dataIndex]
                            : (x.label ?? x.title ?? 'View');
                        return (
                            <Button
                                type="link"
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    options?.onComponentAction?.(x.component, record, x);
                                }}
                            >
                                {labelText}
                            </Button>
                        );
                    }
                };
                columns.push(colConfig);
            } else if (!(x.dataType && (x.dataType == 'date')) && !(x.format && x.format == 'Currency')) {
                let colConfig = {
                        title: x.title,
                        dataIndex: x.dataIndex,
                    hidden: x.hidden ?? false,
                        sorter: x.sorter
                        ? (a, b) =>
                            x.dataType === 'string'
                                ? (a[ x.dataIndex ] || "").localeCompare(b[ x.dataIndex ] || "")
                                : (a[ x.dataIndex ] ?? 0) - (b[ x.dataIndex ] ?? 0)
                        : false,
                        width: x.width ?? 140,
                        fixed: x.fixed ?? false,
                        align: x.format == 'Number'? 'right': "left",
                        onCell: (record) => ({
                        style: { background: record.oldData && ((record.oldData[x.dataIndex] || record[x.dataIndex]) != '') && record.oldData[x.dataIndex] != record[x.dataIndex] ? "#66B966" : "" }
                        }),
                        render: (text, record) => {
                            if (x.wrapText) {
                                return (
                                    <span className="billing-table-cell-wrap">
                                        {text}
                                    </span>
                                );
                            }

                            return (
                                text && text.length > 20 ?
                                    <Tooltip title={text} placement="topLeft">
                                        <span className="billing-table-cell-ellipsis">
                                            {text}
                                        </span>
                                    </Tooltip>
                                    : text
                            );
                        }
                    }
                if (x.filter) {
                    colConfig = { ...colConfig, ...getColumnSearchProps(x.title, x.dataIndex) }
                }
                columns.push(
                    colConfig
                )
            }
            else if (x.format && x.format == 'Currency') {
                let colConfig = {
                    title: x.title,
                    dataIndex: x.dataIndex,
                    hidden: x.hidden ?? false,
                    sorter: x.sorter ? (a, b) => toSortableNumber(a[x.dataIndex]) - toSortableNumber(b[x.dataIndex]) : false,
                    width: x.width ?? 140,
                    fixed: x.fixed ?? false,
                    onCell: (record) => ({
                        style: { background: record.oldData && ((record.oldData[x.dataIndex] || record[x.dataIndex]) != '') && record.oldData[x.dataIndex] != record[x.dataIndex] ? "#66B966" : "" }
                    }),
                    align: "right", // Align numbers to the right
                    render: (value) =>
                        value == null || isNaN(value) ? '' : new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: "USD",
                        }).format(value)
                }
                if (x.filter) {
                    colConfig = { ...colConfig, ...getColumnSearchProps(x.title, x.dataIndex) }
                }
                columns.push(
                    colConfig
                ) 
            }
            else {
                if (x.dataType == 'date') {
                    let colConfig = {
                            title: x.title,
                            dataIndex: x.dataIndex,
                        hidden: x.hidden ?? false,
                            sorter: x.sorter ? (a, b) => toSortableDate(a[x.dataIndex]) - toSortableDate(b[x.dataIndex]) : false,
                            width: x.width ?? 120,
                            fixed: x.fixed ?? false,
                            onCell: (record) => ({
                                style: { background: record.oldData && record.oldData[x.dataIndex] != record[x.dataIndex] ? "#66B966" : "" }
                            }),
                        render: (text) => text ? dayjs(text).format(x.format) : ''
                        }
                    if (x.filter) {
                        colConfig = { ...colConfig, ...getColumnSearchProps(x.title, x.dataIndex) }
                    }
                    columns.push(
                        colConfig
                    )
                }
            }
        }
    })
    return columns;
}
